
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useApi } from '../../../hooks/useApi';
import apiService from '../../../services/apiService';
import { calculateSimulation } from '../../../utils/simulationCalculator';

import Step1_Vehicle from './Step1_Vehicle';
import Step2_Usage from './Step2_Usage';
import Step3_Installation from './Step3_Installation';
import Step4_UserInfo from './Step4_UserInfo';
import Step5_Summary from './Step5_Summary';
import Results from './Results';

// Define the structure for your form data
interface FormData {
  // Step 1
  vehicleType: string;
  vehicleBrand: string;
  vehicleModel: string;
  vehicleYear: number | string;
  batteryCapacity: number | string;
  // Step 2
  dailyMileage: number | string;
  parkingLocation: string;
  installationType: string;
  desiredPower: string;
  // Step 3
  distanceToPanel: number | string;
  meterType: string;
  meterPower: number | string;
  roadworkNeeded: string;
  // Step 4
  name: string;
  email: string;
  phone: string;
  postalCode: string;
  acceptGdpr: boolean;
}

const SuccessMessage = ({ email }) => (
    <div className="text-center py-10">
        <svg className="w-16 h-16 mx-auto text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
        <h2 className="mt-4 text-2xl font-bold text-gray-800">Simulation envoyée !</h2>
        <p className="mt-2 text-gray-600">
            Merci ! Un résumé de votre simulation a été envoyé à l'adresse <span className="font-semibold text-gray-900">{email}</span>.
        </p>
        <p className="mt-1 text-gray-600">Nos équipes vous contacterons prochainement.</p>
        <button 
            onClick={() => window.location.reload()}
            className="mt-8 px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
        >
            Faire une autre simulation
        </button>
        <Link to="/" className="block mt-4 text-blue-600 hover:underline">Retour au site web</Link>
    </div>
);


const Simulator = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    vehicleType: 'Voiture électrique',
    vehicleBrand: '',
    vehicleModel: '',
    vehicleYear: '',
    batteryCapacity: '',
    dailyMileage: '',
    parkingLocation: 'Maison individuelle',
    installationType: 'Intérieur garage',
    desiredPower: '7.4 kW',
    distanceToPanel: '',
    meterType: 'Monophasé',
    meterPower: '',
    roadworkNeeded: 'Non',
    name: '',
    email: '',
    phone: '',
    postalCode: '',
    acceptGdpr: false,
  });
  const [results, setResults] = useState(null);
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, answer: '' });

  // Generate captcha numbers only once
  useMemo(() => {
    setCaptcha(c => ({ ...c, num1: Math.floor(Math.random() * 10), num2: Math.floor(Math.random() * 10) }));
  }, []);

  const { execute: sendSimulation, isLoading } = useApi(apiService.simulation.send);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
        const { checked } = e.target as HTMLInputElement;
        setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCaptchaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setCaptcha(c => ({ ...c, answer: e.target.value }));
  }

  const nextStep = () => setCurrentStep(prev => prev + 1);
  const prevStep = () => setCurrentStep(prev => prev - 1);

  // Step 1: Calculate results and show them
  const handleShowResults = (e: React.FormEvent) => {
    e.preventDefault();
    // Captcha validation
    if (parseInt(captcha.answer, 10) !== captcha.num1 + captcha.num2) {
        toast.error('La réponse à la question de sécurité est incorrecte.');
        return;
    }
    if (!formData.acceptGdpr) {
        toast.error('Vous devez accepter la politique de confidentialité.');
        return;
    }
    const calculatedResults = calculateSimulation(formData);
    setResults(calculatedResults);
    setCurrentStep(6); // Go to results page
  };

  // Step 2: Confirm and send the email with all data
  const handleConfirmAndSend = async () => {
    const payload = {
        formData,
        results,
    };

    const loadingToast = toast.loading('Envoi de votre simulation...');
    try {
        await sendSimulation(payload);
        toast.dismiss(loadingToast);
        toast.success('Simulation envoyée avec succès !');
        setIsSubmitted(true);
    } catch (error) {
        toast.dismiss(loadingToast);
        toast.error('Une erreur est survenue. Veuillez réessayer.');
        console.error(error);
    }
  }

  const renderStep = () => {
    if (results && currentStep === 6) {
        return <Results 
            results={results} 
            handleConfirmAndSend={handleConfirmAndSend} 
            isLoading={isLoading}
            prevStep={() => {
                setResults(null);
                setCurrentStep(5);
            }} 
        />
    }

    switch (currentStep) {
      case 1:
        return <Step1_Vehicle formData={formData} setFormData={setFormData} handleChange={handleChange} nextStep={nextStep} />;
      case 2:
        return <Step2_Usage formData={formData} handleChange={handleChange} nextStep={nextStep} prevStep={prevStep} />;
      case 3:
        return <Step3_Installation formData={formData} handleChange={handleChange} nextStep={nextStep} prevStep={prevStep} />;
      case 4:
        return <Step4_UserInfo formData={formData} handleChange={handleChange} nextStep={handleShowResults} prevStep={prevStep} captcha={captcha} handleCaptchaChange={handleCaptchaChange} />;
      case 5:
        return <Step5_Summary formData={formData} prevStep={prevStep} handleSubmit={handleShowResults} />;
      default:
        return <Step1_Vehicle formData={formData} setFormData={setFormData} handleChange={handleChange} nextStep={nextStep} />;
    }
  };

  return (

    <div className="bg-gray-50 min-h-screen flex items-center justify-center p-4 font-sans" style={{fontFamily: '\'Lato\', sans-serif'}}>
        
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-lg p-8 transition-all duration-500">
        <div className="inline-flex items-center justify-center mb-4">
            <img src="/logo.png" alt="Elecborn Logo" className="h-16" />
          </div>
        {isSubmitted ? (
            <SuccessMessage email={formData.email} />
        ) : (
            <>
                <Link to="/" className="text-blue-600 hover:underline mb-4 inline-block">&larr; Retour au site web</Link>
                <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">Simulateur de borne de recharge</h1>
                <p className="text-center text-gray-500 mb-8">Estimez votre projet en quelques étapes.</p>
                
                {/* Hide progress bar on results page */}
                {currentStep !== 6 && (
                    <div className="relative mb-8 h-2 bg-gray-200 rounded-full">
                        <div 
                            className="absolute top-0 left-0 h-full bg-green-500 rounded-full transition-all duration-500 ease-in-out"
                            style={{ width: `${((currentStep - 1) / 4) * 100}%` }}
                        ></div>
                    </div>
                )}

                <form onSubmit={handleShowResults}>
                  {renderStep()}
                </form>
            </>
        )}
      </div>
    </div>

  );
};

export default Simulator;
